import React, { useState } from "react";
import {
  Box,
  Grid,
  Menu,
  MenuItem,
  Pagination,
  TextField,
  Typography,
} from "@mui/material";
import { Button } from "@mui/material";
import SpecRecipeList from "./components/SpecRecipeList";
import SpecSpinner from "../../components/SpecSpinner";
import useSpecRecipesPaginated from "../../hooks/useSpecRecipesPaginated";
import useSpecRecipePaginatedSearch from "../../hooks/useSpecRecipePaginatedSearch";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { cloneDeep, debounce } from "lodash";

const SpecRecipes = () => {
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [localSearchText, setLocalSearchText] = useState("");
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [filters, setFilters] = useState({
    sortAsc: true,
    sortByCreatedDate: false,
    tagFilters: [],
    showInactive: false,
  });

  const disableBaseData = searchText ? true : false;
  const { data: paginatedData, isLoading: isLoadingPaginated } =
    useSpecRecipesPaginated(page, filters, disableBaseData);
  const { data: searchData, isLoading: isLoadingSearch } =
    useSpecRecipePaginatedSearch(searchText, page, filters);

  const handlePageSet = (_, newPage) => {
    setPage(newPage);
  };

  const handleSortAZ = () => {
    let sortObj = cloneDeep(filters);
    sortObj.sortAsc = true;
    sortObj.sortByCreatedDate = false;
    setFilters(sortObj);
    setPage(1);
    handleCloseSortMenu();
  };

  const handleSortZA = () => {
    let sortObj = cloneDeep(filters);
    sortObj.sortAsc = false;
    sortObj.sortByCreatedDate = false;
    setFilters(sortObj);
    setPage(1);
    handleCloseSortMenu();
  };

  const handleSortNewestOldest = () => {
    let sortObj = cloneDeep(filters);
    sortObj.sortAsc = true;
    sortObj.sortByCreatedDate = true;
    setFilters(sortObj);
    setPage(1);
    handleCloseSortMenu();
  };

  const handleSortOldestNewest = () => {
    let sortObj = cloneDeep(filters);
    sortObj.sortAsc = false;
    sortObj.sortByCreatedDate = true;
    setFilters(sortObj);
    setPage(1);
    handleCloseSortMenu();
  };

  const handleCloseSortMenu = () => {
    setSortMenuAnchor(null);
  };

  const handleOpenSortMenu = (e) => {
    setSortMenuAnchor(e.currentTarget);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const debouncedSearchHandler = debounce(handleSearch, 300);

  const handleSearchChange = (e) => {
    setLocalSearchText(e.target.value);
    debouncedSearchHandler.cancel();
    setPage(1);
    debouncedSearchHandler(e);
  };

  if (isLoadingPaginated && !paginatedData) {
    return <SpecSpinner open text={"Loading Spec Recipes..."} />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "2440px",
        padding: 4,
        marginTop: 6,
      }}
    >
      <Box sx={{ marginBottom: 6 }}>
        <Grid
          container
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Grid item xs={12}>
            <Typography variant="h2">Spec Recipes</Typography>
          </Grid>
        </Grid>
      </Box>
      <Grid
        container
        direction="row"
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                autoComplete="off"
                id="specRecipeSearch"
                onChange={handleSearchChange}
                value={localSearchText}
                inputProps={{
                  sx: {
                    marginBottom: 1,
                  },
                }}
                sx={{
                  backgroundColor: (theme) => theme.palette.pure.white,
                  borderColor: (theme) => theme.palette.terrain[400],
                  width: "340px",
                  borderRadius: 1,
                }}
                label={
                  <>
                    <SearchOutlinedIcon style={{ paddingRight: "5px" }} />
                    Search for recipes...
                  </>
                }
              />
            </Box>
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: { xs: "flex-end", md: "flex-end" },
            marginTop: { xs: 4, md: 0 },
          }}
        >
          <Box>
            <Button
              variant="outlined"
              sx={{ marginRight: 1, marginLeft: 2 }}
              onClick={handleOpenSortMenu}
            >
              <FilterListIcon sx={{ fontSize: 18, marginRight: 2 }} />
              <Typography variant="largeButton">Sort</Typography>
            </Button>
            <Menu
              anchorEl={sortMenuAnchor}
              open={!!sortMenuAnchor}
              onClose={handleCloseSortMenu}
            >
              <MenuItem
                sx={{ margin: "5px" }}
                selected={filters.sortAsc && !filters.sortByCreatedDate}
                onClick={handleSortAZ}
              >
                Name A-Z
              </MenuItem>
              <MenuItem
                sx={{ margin: "5px" }}
                selected={!filters.sortAsc && !filters.sortByCreatedDate}
                onClick={handleSortZA}
              >
                Name Z-A
              </MenuItem>
              <MenuItem
                sx={{ margin: "5px" }}
                selected={filters.sortAsc && filters.sortByCreatedDate}
                onClick={handleSortNewestOldest}
              >
                Newest First
              </MenuItem>
              <MenuItem
                sx={{ margin: "5px" }}
                selected={!filters.sortAsc && filters.sortByCreatedDate}
                onClick={handleSortOldestNewest}
              >
                Oldest First
              </MenuItem>
            </Menu>
          </Box>
        </Grid>
      </Grid>

      {searchText ? (
        <SpecRecipeList recipes={searchData?.recipes} isLoading={isLoadingSearch} />
      ) : (
        <SpecRecipeList
          recipes={paginatedData?.recipes}
          isLoading={isLoadingPaginated}
        />
      )}
      <Pagination
        sx={{ marginTop: 6 }}
        count={
          searchText ? searchData?.totalPages : paginatedData?.totalPages
        }
        onChange={handlePageSet}
        page={page}
      />
    </Box>
  );
};

export default SpecRecipes;
